export interface ChipFilterDimension<T> {
  key: string;
  extract: (item: T) => string[];
  sort?: (a: string, b: string) => number;
}

export interface ChipFilterState {
  inputValue: string;
  selected: string[];
  filtered: string[];
}

export class ChipFilterGroup<T> {
  readonly state: { [key: string]: ChipFilterState } = {};
  private allItems: { [key: string]: string[] } = {};
  private available: { [key: string]: string[] } = {};
  private rawData: T[] = [];

  constructor(private dimensions: ChipFilterDimension<T>[]) {
    for (const d of dimensions) {
      this.state[d.key] = { inputValue: '', selected: [], filtered: [] };
      this.allItems[d.key] = [];
      this.available[d.key] = [];
    }
  }

  populate(data: T[]): void {
    this.rawData = data;
    for (const d of this.dimensions) {
      const set = new Set<string>();
      for (const item of data) {
        for (const v of d.extract(item)) {
          if (v) set.add(v);
        }
      }
      const sorted = Array.from(set);
      sorted.sort(d.sort ?? ((a, b) => a.localeCompare(b)));
      this.allItems[d.key] = sorted;
      this.available[d.key] = [...sorted];
      this.state[d.key].filtered = [...sorted];
      this.state[d.key].selected = [];
      this.state[d.key].inputValue = '';
    }
  }

  select(key: string, value: string): void {
    const s = this.state[key];
    if (!value || s.selected.includes(value)) return;
    s.selected.push(value);
    s.inputValue = '';
    this.updateAvailableForAll(key);
  }

  remove(key: string, value: string): void {
    const s = this.state[key];
    const idx = s.selected.indexOf(value);
    if (idx < 0) return;
    s.selected.splice(idx, 1);
    this.updateAvailableForAll(key);
  }

  onInput(key: string, text: string): void {
    this.state[key].inputValue = text;
    const lower = text.toLowerCase();
    this.state[key].filtered = this.available[key].filter(
      v => v.toLowerCase().includes(lower) && !this.state[key].selected.includes(v)
    );
  }

  clear(): void {
    for (const d of this.dimensions) {
      this.state[d.key].selected = [];
      this.state[d.key].inputValue = '';
      this.available[d.key] = [...this.allItems[d.key]];
      this.state[d.key].filtered = [...this.allItems[d.key]];
    }
  }

  hasAnySelected(): boolean {
    return this.dimensions.some(d => this.state[d.key].selected.length > 0);
  }

  apply(data: T[]): T[] {
    return data.filter(item => {
      for (const d of this.dimensions) {
        const selected = this.state[d.key].selected;
        const inputValue = (this.state[d.key].inputValue ?? '').trim();
        const values = d.extract(item);

        if (inputValue) {
          // While the user is typing: filter rows by partial text match (real-time preview).
          // This takes precedence over chip selection for this dimension.
          const lower = inputValue.toLowerCase();
          if (!values.some(v => v.toLowerCase().includes(lower))) return false;
        } else if (selected.length > 0) {
          // Chip filter: row must match at least one selected chip.
          if (!selected.some(s => values.includes(s))) return false;
        }
      }
      return true;
    });
  }

  private updateAvailableForAll(changedKey: string): void {
    for (const d of this.dimensions) {
      this.updateAvailableFor(d.key, changedKey);
    }
  }

  private updateAvailableFor(key: string, changedKey: string): void {
    const dim = this.dimensions.find(d => d.key === key)!;
    const otherDims = this.dimensions.filter(d => d.key !== key);
    const hasOtherSelected = otherDims.some(d => this.state[d.key].selected.length > 0);

    if (!hasOtherSelected) {
      this.available[key] = [...this.allItems[key]];
    } else {
      const set = new Set<string>();
      for (const item of this.rawData) {
        let ok = true;
        for (const od of otherDims) {
          const sel = this.state[od.key].selected;
          if (sel.length === 0) continue;
          const vals = od.extract(item);
          if (!sel.some(s => vals.includes(s))) {
            ok = false;
            break;
          }
        }
        if (ok) {
          for (const v of dim.extract(item)) {
            if (v) set.add(v);
          }
        }
      }
      const arr = Array.from(set);
      arr.sort(dim.sort ?? ((a, b) => a.localeCompare(b)));
      this.available[key] = arr;
    }

    const lower = this.state[key].inputValue.toLowerCase();
    this.state[key].filtered = this.available[key].filter(
      v => v.toLowerCase().includes(lower) && !this.state[key].selected.includes(v)
    );
  }
}
